import { message } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { mobile } from "@btnet/utilities-webview";

import { ClientObservableBackgroundJobStatus } from "types/enum";

import { downloadUrlViaWebviewOrAnchor } from "utilities/document/fileDownload.utils";

import { openBackgroundProcessStatusPopup } from "commonComponents/utilities/ClientObservableBackgroundJobStatusPopup/ClientObservableBackgroundJobStatusPopup";
import { ClientObservableBackgroundJobStatusPopupHandler } from "commonComponents/utilities/ClientObservableBackgroundJobStatusPopup/ClientObservableBackgroundJobStatusPopup.api.handler";
import { ProcessStatusGroupType } from "commonComponents/utilities/ClientObservableBackgroundJobStatusPopup/ClientObservableBackgroundJobStatusPopup.types";

import { plansDownloadSetBySetIdPostV2, plansExternalSetsDownloadSetBySetIdPostV2 } from "handlers";

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 120_000;

export interface IUseDownloadPlanSetArgs {
    setId: string;
    jobId: number | null;
    shareToken?: string;
    bidId?: number;
}

export interface IUseDownloadPlanSetResult {
    download: (includeAnnotations: boolean) => Promise<void>;
    isWorking: boolean;
}

/**
 * POSTs the download request and routes the response to one of four flows:
 *   - downloadUrl + external caller: HEAD-poll the URL, then download.
 *   - downloadUrl + internal cache hit: download immediately.
 *   - processId + webview: poll the background-job status endpoint inline until
 *     the worker writes Success, then download. The status popup is mounted by
 *     MainNavigation, which webview routes never render, so the popup-driven
 *     auto-download can't be used here.
 *   - processId + browser (internal cache miss): open the background-job popup;
 *     the PlanSetDownloadItem renderer triggers the actual download once the
 *     worker writes Success.
 *
 * All download paths go through `downloadUrlViaWebviewOrAnchor` so a mobile
 * webview hands the file to the native share sheet — `<a download>` does nothing
 * in iOS WKWebView.
 */
export function useDownloadPlanSet({
    setId,
    jobId,
    shareToken,
    bidId,
}: IUseDownloadPlanSetArgs): IUseDownloadPlanSetResult {
    const [isWorking, setIsWorking] = useState(false);
    const cancelledRef = useRef(false);
    const backgroundJobHandler = useMemo(
        () => new ClientObservableBackgroundJobStatusPopupHandler(),
        [],
    );

    useEffect(() => {
        return () => {
            cancelledRef.current = true;
        };
    }, []);

    const download = useCallback(
        async (includeAnnotations: boolean) => {
            if (isWorking) {
                return;
            }
            cancelledRef.current = false;
            setIsWorking(true);

            try {
                const resp = shareToken
                    ? await plansExternalSetsDownloadSetBySetIdPostV2(setId, {
                          includeAnnotations,
                      })
                    : await plansDownloadSetBySetIdPostV2(setId, {
                          jobId: jobId ?? undefined,
                          includeAnnotations,
                          ...(bidId !== undefined ? { bidId } : {}),
                      });
                if (cancelledRef.current) {
                    return;
                }

                const { downloadUrl, processId, fileName } = resp.data;

                if (downloadUrl) {
                    // External callers may need to wait for the worker to upload;
                    // HEAD-poll before downloading. Internal cache hits also fall
                    // here — the first poll succeeds immediately.
                    if (shareToken) {
                        const ready = await waitForFile(downloadUrl, () => cancelledRef.current);
                        if (cancelledRef.current) return;
                        if (!ready) {
                            void message.error("Plan set download timed out. Please try again.");
                            return;
                        }
                    }
                    const triggered = await downloadUrlViaWebviewOrAnchor(
                        downloadUrl,
                        fileName ?? undefined,
                    );
                    if (!triggered) {
                        void message.error("Plan set download URL was rejected.");
                        return;
                    }
                    if (!cancelledRef.current) {
                        void message.success("Plan set downloaded");
                    }
                    return;
                }

                if (processId) {
                    if (mobile.isWebview) {
                        void message.info("Preparing download");
                        const job = await waitForBackgroundJob(
                            backgroundJobHandler,
                            processId,
                            () => cancelledRef.current,
                        );
                        if (cancelledRef.current) return;
                        if (!job?.downloadUrl) {
                            void message.error("Plan set download timed out. Please try again.");
                            return;
                        }
                        const triggered = await downloadUrlViaWebviewOrAnchor(
                            job.downloadUrl,
                            job.fileName ?? fileName ?? undefined,
                        );
                        if (!triggered) {
                            void message.error("Plan set download URL was rejected.");
                            return;
                        }
                        if (!cancelledRef.current) {
                            void message.success("Plan set downloaded");
                        }
                        return;
                    }

                    // Internal cache miss — popup tracks progress and fires the
                    // anchor download on Success.
                    openBackgroundProcessStatusPopup(ProcessStatusGroupType.PlanSetDownload);
                    void message.info("Preparing download");
                    return;
                }

                void message.error("Plan set download didn't return a file URL or job id.");
            } catch {
                if (!cancelledRef.current) {
                    void message.error("Failed to start plan set download.");
                }
            } finally {
                if (!cancelledRef.current) {
                    setIsWorking(false);
                }
            }
        },
        [isWorking, setId, jobId, shareToken, bidId, backgroundJobHandler],
    );

    return { download, isWorking };
}

/**
 * Polls the merged-PDF URL with HEAD until it returns 200. Cache hits succeed
 * immediately; cache misses wait for the Hangfire worker to upload.
 */
async function waitForFile(url: string, isCancelled: () => boolean): Promise<boolean> {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (!isCancelled()) {
        try {
            const resp = await fetch(url, { method: "HEAD", credentials: "same-origin" });
            if (resp.ok) {
                return true;
            }
        } catch {
            // network blip — fall through to backoff
        }
        if (Date.now() >= deadline) {
            return false;
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }
    return false;
}

interface ICompletedPlanSetDownloadJob {
    downloadUrl?: string;
    fileName?: string;
}

/**
 * Polls the background-job status endpoint until the plan-set download job reaches
 * a terminal state, and returns its result payload on Success.
 *
 * The client can't build the merged-file URL itself — the route includes a
 * server-computed content hash — so the job's `additionalData` is the only way to
 * learn where the file landed.
 *
 * @returns the job payload on Success, or undefined on failure/timeout/cancel.
 */
async function waitForBackgroundJob(
    handler: ClientObservableBackgroundJobStatusPopupHandler,
    processId: string,
    isCancelled: () => boolean,
): Promise<ICompletedPlanSetDownloadJob | undefined> {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (!isCancelled()) {
        try {
            const statuses = await handler.getClientObservableBackgroundJobStatuses();
            const job = statuses.backgroundJobStatuses.find((j) => j.processID === processId);
            if (job) {
                if (job.status === ClientObservableBackgroundJobStatus.Success) {
                    return {
                        downloadUrl: job.additionalData?.downloadUrl,
                        fileName: job.additionalData?.fileName,
                    };
                }
                if (
                    job.status === ClientObservableBackgroundJobStatus.Failed ||
                    job.status === ClientObservableBackgroundJobStatus.Deleted
                ) {
                    return undefined;
                }
            }
        } catch {
            // network blip — fall through to backoff
        }
        if (Date.now() >= deadline) {
            return undefined;
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }
    return undefined;
}
